import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const glassCardVariants = cva(
  cn(
    'relative rounded-2xl border',
    'shadow-[0_8px_32px_rgba(0,0,0,0.12)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.5)]',
    'before:absolute before:inset-0 before:rounded-2xl',
    'before:bg-linear-to-b before:from-white/[0.06] before:to-transparent before:pointer-events-none',
    'after:absolute after:inset-px after:rounded-[calc(1rem-1px)]',
    'after:shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)] after:pointer-events-none'
  ),
  {
    variants: {
      mode: {
        glass: cn(
          'border-black/10 dark:border-white/[0.08]',
          'bg-white/45 dark:bg-white/[0.20] backdrop-blur-xl'
        ),
        solid: cn(
          'border-gray-200 dark:border-gray-700',
          'bg-white dark:bg-gray-800',
          'backdrop-blur-none',
          'before:hidden after:hidden'
        ),
      },
    },
    defaultVariants: {
      mode: 'glass',
    },
  }
)

interface GlassCardProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof glassCardVariants> {
  glowEffect?: boolean
  children: React.ReactNode
}

const GlassCard = React.forwardRef<HTMLDivElement, GlassCardProps>(
  ({ className, mode, glowEffect = true, children, ...props }, ref) => {
    const isFullHeight = typeof className === 'string' && className.includes('h-full')
    return (
      <div className={isFullHeight ? 'relative h-full flex flex-col' : 'relative'}>
        {glowEffect && mode === 'glass' && (
          <div className="absolute -inset-1 rounded-2xl bg-linear-to-r from-cyan-500/10 via-blue-500/10 to-purple-500/10 blur-xl opacity-50" />
        )}
        <div
          ref={ref}
          className={cn(glassCardVariants({ mode, className }), isFullHeight ? 'flex flex-col' : '')}
          {...props}
        >
          <div className={isFullHeight ? 'relative z-10 flex flex-col flex-1 min-h-0' : 'relative z-10'}>{children}</div>
        </div>
      </div>
    )
  }
)
GlassCard.displayName = 'GlassCard'

const GlassCardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col gap-1.5 p-6', className)}
    {...props}
  />
))
GlassCardHeader.displayName = 'GlassCardHeader'

const GlassCardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'text-xl font-semibold leading-none tracking-tight text-foreground',
      className
    )}
    {...props}
  />
))
GlassCardTitle.displayName = 'GlassCardTitle'

const GlassCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
GlassCardDescription.displayName = 'GlassCardDescription'

const GlassCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
))
GlassCardContent.displayName = 'GlassCardContent'

const GlassCardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-6 pt-0', className)}
    {...props}
  />
))
GlassCardFooter.displayName = 'GlassCardFooter'

export {
  GlassCard,
  GlassCardHeader,
  GlassCardTitle,
  GlassCardDescription,
  GlassCardContent,
  GlassCardFooter,
}
